<script setup lang="ts">
import type { InfoData } from '~/queries'
import UiCarousel from '@boats/ui/components/UiCarousel.vue'
import UiSearchBarButton from '@boats/ui/components/UiSearchBarButton.vue'
import UiChip from '@boats/ui/components/UiChip.vue'
import { useLocationStore } from '~/stores/location'

const props = defineProps<{
  page: InfoData['page']
}>()

const modals = useModalsStore()
const favourites = useFavouritesStore()
const location = useLocationStore()

const scrollToNewsletter = (action: any) => {
  if (action === 'scroll_subscribe_to_newsletter') {
    const el = document.getElementById('app-newsletter-section');
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 84;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }
}

const sections = ref(props.page?.content?.data?.sections)
const dividerExcludeNextSafeNames = ['banner_image']

const normalizePercent = (value: unknown, fallback: number) => {
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return fallback
  return Math.min(100, Math.max(0, parsed))
}

const normalizeBannerLink = (value: unknown) => {
  if (typeof value !== 'string') return ''
  return value.trim()
}

// Blank → the kicker's default theme red (set in CSS), so only a valid #RRGGBB is applied.
const normalizeBannerColour = (value: unknown) =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value) ? value : ''

const isSplitPanelBanner = (section: any) => section?.data?.design === 'split_panel'

const getBannerSlides = (section: any) => {
  const slides = Array.isArray(section?.data?.slides) ? section.data.slides : []

  return slides
    .map((slide: any, slideIndex: number) => ({
      id: slide.id || `banner-image-slide-${slideIndex}`,
      image: typeof slide.image === 'string' ? slide.image : slide.image?.url,
      alt: slide.image?.alt_text || 'Banner image',
      header: slide.header || '',
      mainText: slide.main_text || '',
      subText: slide.sub_text || '',
      moreInfoLink: normalizeBannerLink(slide.more_info_link),
      linkText: typeof slide.link_text === 'string' ? slide.link_text.trim() : '',
      headerColour: normalizeBannerColour(slide.header_colour),
      objectPositionX: normalizePercent(slide.object_position_x, 50),
      objectPositionY: normalizePercent(slide.object_position_y, 0),
      useImageOverlay: slide.use_image_overlay !== false,
    }))
    .filter((slide: any) => !!slide.image)
}

const getBannerAutoPlay = (section: any) => {
  const seconds = Number(section?.data?.auto_rotate_seconds)
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : false
}

const getBannerCarouselOptions = (section: any) => {
  const slidesCount = getBannerSlides(section).length
  return {
    autoPlay: slidesCount > 1 ? getBannerAutoPlay(section) : false,
    prevNextButtons: false,
    draggable: true,
    pageDots: false,
    adaptiveHeight: false,
  }
}

const shouldUseSpacingInsteadOfDivider = (sectionIndex: number) => {
  const nextSection = sections.value?.[sectionIndex + 1]
  if (!nextSection?.safe_name) return false
  return dividerExcludeNextSafeNames.includes(nextSection.safe_name)
}

</script>

<template>
  <div>
    <template v-for="(section, index) in sections" :key="index">
      <template v-if="section.safe_name === 'banner_image'">
        <section
          v-if="getBannerSlides(section).length"
          class="banner-image mb-12 lg:mb-16"
          :class="{ 'banner-image--split': isSplitPanelBanner(section) }"
        >
          <UiCarousel
            class="banner-image__carousel"
            :options="getBannerCarouselOptions(section)"
          >
            <header
              v-for="slide in getBannerSlides(section)"
              :key="slide.id"
              class="banner-image__hero banner-image__item overflow-hidden flex-shrink-0"
            >
              <div class="banner-image__image">
                <img
                  class="banner-image__img"
                  :src="slide.image"
                  :alt="slide.alt"
                  :style="{ objectPosition: `${slide.objectPositionX}% ${slide.objectPositionY}%` }"
                  loading="eager"
                  decoding="async"
                  draggable="false"
                />
              </div>

              <div v-if="isSplitPanelBanner(section)" class="banner-image__fade"></div>
              <div v-else-if="slide.useImageOverlay" class="banner-image__overlay"></div>

              <NuxtLink
                v-if="slide.moreInfoLink"
                class="banner-image__full-link"
                :to="slide.moreInfoLink"
                :aria-label="slide.mainText || slide.header || $t('string.More-info')"
              ></NuxtLink>

              <div class="banner-image__content">
                <p
                  v-if="slide.header"
                  class="banner-image__kicker text-button"
                  :style="slide.headerColour ? { color: slide.headerColour } : undefined"
                >
                  {{ slide.header }}
                </p>

                <h1 class="banner-image__title text-balance">{{ slide.mainText }}</h1>

                <p v-if="slide.subText" class="banner-image__sub-text">
                  {{ slide.subText }}
                </p>

                <NuxtLink
                  v-if="slide.moreInfoLink && isSplitPanelBanner(section)"
                  class="banner-image__button"
                  :to="slide.moreInfoLink"
                >
                  {{ slide.linkText || $t('string.More-info') }}
                </NuxtLink>
              </div>

              <NuxtLink
                v-if="slide.moreInfoLink && !isSplitPanelBanner(section)"
                class="banner-image__cta text-button"
                :to="slide.moreInfoLink"
              >
                <span>{{ slide.linkText || $t('string.More-info') }}</span>
                <span class="banner-image__cta-icon" aria-hidden="true">
                  <IconChevronRight />
                </span>
              </NuxtLink>
            </header>
          </UiCarousel>
        </section>
      </template>

      <template v-else-if="section.safe_name === 'headline'">
        <div class="mb-12">
          <header class="row justify-between lg:mb-14">
            <div class="col-lg-5 mb-10 lg:mb-0">
              <h1 class="mb-3 text-balance md:mb-3">
                {{ section.data.title }}
              </h1>

              <p class="lead">
                {{ section.data.description }}
              </p>
            </div>

            <div class="col-lg-6">
              <AppImage
                :src="section.data.image.url"
                :alt="section.data.image.alt || section.data.title"
                :style="{cursor: section.data?.on_click_action === 'scroll_subscribe_to_newsletter' ? 'pointer' : 'default'}"
                @click="scrollToNewsletter(section.data?.on_click_action)"
              />
            </div>
          </header>

          <div class="relative -mt-6 px-3 md:-mt-10 md:px-5 lg:mt-0 lg:px-0">
            <div class="row justify-center">
              <div class="col-lg-10">
                <AppDivider
                  class="absolute left-1/2 top-1/2 hidden w-svw -translate-x-1/2 lg:block"
                />

                <UiSearchBarButton
                  class="relative"
                  @click="modals.openSearchModal"
                />
              </div>
            </div>
          </div>
        </div>
      </template>

      <AppCarouselCard
        v-else-if="
            section.safe_name === 'named_collection' &&
            section.data.collection_type === 'small_list'
          "
      >
        <template #title>{{ section.data.name }}</template>

        <template
          v-for="(item, itemIndex) in section.data.items.filter(i => !(i.not_allowed_in || []).includes(location.data?.country_code))"
          :key="item"
        >
          <div class="carousel-item">
            <AppCard

              :id="item.item.data.id"
              :type="item.type"
              :to="cleanPath('/', item.item.data.url)"
              :title="item.item.data.name"
              :image="{ src: item.item.data.image_extra?.url_home_thumb ?? item.item.data.image, alt: item.item.data.name }"
              :favourited="item.item.data.favourited"
              class="relative"
            >
              <div
                v-if="section.data.enumeration"
                class="h1 alt text-stroke-white absolute left-3 top-0 z-10 -translate-y-1/2"
                aria-hidden="true"
              >
                {{ itemIndex + 1 }}
              </div>
              <p
                v-if="item.type === 'product' && item.item.data.type === 'football_match'"
              >
                {{ formatDate(item.item.data.start_date) }}
              </p>
              <p
                v-else-if="item.type === 'product' && item.item.data.starting_price"
              >
                {{ $t('string.Tickets-from') }}
                <UiChip variant="price">
                  {{ formatCurrency(item.item.data.starting_price) }}
                </UiChip>
              </p>
            </AppCard>
          </div>
        </template>
      </AppCarouselCard>

      <AppCarouselCard
        v-else-if="
            section.safe_name === 'named_collection' &&
            section.data.collection_type === 'large_list'
          "
        class="large-items"
        :paper="false"
        :auto-play="3000"
      >
        <template #title>{{ section.data.name }}</template>

        <template
          v-for="item in section.data.items.filter(i => !(i.not_allowed_in || []).includes(location.data?.country_code))"
          :key="item.item.data.id"
        >
          <div class="carousel-item">
            <AppCardLarge
              :id="item.item.data.id"
              :type="item.type"
              :to="cleanPath('/', item.item.data.url)"
              :title="item.item.data.name"
              :image="{ src: item.item.data.image, alt: item.item.data.name }"
              :favourited="item.item.data.favourited"
              class="overflow-hidden"
            >
              <template #meta>
                <p
                  v-if="item.type === 'product' && item.item.data.type === 'football_match'"
                >
                  {{ item.item?.competitions?.at(0)?.data?.name }}
                </p>
                <p
                  v-if="item.type === 'product' && item.item.data.type === 'football_match'"
                >
                  {{ formatDate(item.item.data.start_date) }}
                </p>
                <p
                  v-else-if="
                    item.type === 'product' && item.item.data.starting_price
                  "
                >
                  {{ $t('string.Tickets-from') }}
                  <UiChip variant="price">
                    {{ formatCurrency(item.item.data.starting_price) }}
                  </UiChip>
                </p>
              </template>

              <div
                class="line-clamp-3"
                v-html="item.item.data.description"
              ></div>
            </AppCardLarge>
          </div>
        </template>
      </AppCarouselCard>
      <section
        v-else-if="
            section.safe_name === 'named_collection' &&
            section.data.collection_type === 'link_list'
          "
        class="row"
      >
        <div class="col-lg-3 mb-8 flex flex-col lg:mb-0">
          <h2 class="alt">{{ section.data.name }}</h2>
        </div>

        <div class="col-lg-9">
          <template
            v-for="(item, index) in section.data.items.filter(i => !(i.not_allowed_in || []).includes(location.data?.country_code))"
            :key="item.item.data.id">
            <NuxtLink
              class="flex items-center border-b border-gray-200 py-5 first:pt-0 last:border-b-0 last:pb-0 hover:no-underline"
              :to="cleanPath('/', item.item.data.url)"
            >
                <span class="h3 w-[1.75rem] flex-shrink-0 md:w-[6.25rem]">
                  {{ index + 1 }}.
                </span>

              <AppImage
                :src="item.item.data.image"
                :alt="item.item.data.name"
                class="mr-3 h-[2.75rem] w-[2.75rem] flex-shrink-0 rounded object-cover md:mr-6"
                ratio="1:1"
              ></AppImage>

              <h3 class="mr-3 md:mr-6">{{ item.item.data.name }}</h3>

              <AppButtonLike
                class="white ml-auto flex-shrink-0"
                :active="!!item.item.data.favourited"
                @click="
                    async (event) => {
                      event.preventDefault()

                      await favourites.toggleFavourite({
                        id: item.item.data.id,
                        type: item.type,
                        favouriteId: item.item.data.favourited,
                      })
                    }
                  "
              />
            </NuxtLink>
          </template>
        </div>
      </section>

      <section v-else-if="section.safe_name === 'testimonials'" class="row">
        <div class="col-lg-3"></div>

        <div class="col-lg-7 relative">
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="55"
              height="43"
              viewBox="0 0 55 43"
              fill="none"
              class="absolute -top-2 right-full mr-6"
            >
              <path
                d="M37.6152 1.50433C37.8416 0.619176 38.6391 0 39.5528 0H52.424C53.7296 0 54.6852 1.23074 54.3616 2.49567L44.3848 41.4957C44.1584 42.3808 43.3609 43 42.4472 43H29.576C28.2704 43 27.3148 41.7693 27.6384 40.5043L37.6152 1.50433Z"
                fill="#121318"
              />
              <path
                d="M10.6152 1.50433C10.8416 0.619176 11.6391 0 12.5528 0H25.424C26.7296 0 27.6852 1.23074 27.3616 2.49567L17.3848 41.4957C17.1584 42.3808 16.3609 43 15.4472 43H2.57603C1.27038 43 0.314841 41.7693 0.638426 40.5043L10.6152 1.50433Z"
                fill="#121318"
              />
            </svg>

            <p class="h1 alt mb-10">
              {{ section.data?.[0]?.main_text }}
            </p>

            <span class="h4">{{ section.data?.[0]?.author_name }}</span>

            <p class="lead">{{ section.data?.[0]?.purchase_details }}</p>
          </div>
        </div>
      </section>

      <section v-else-if="section.safe_name === 'about_us'" class="row">
        <div class="col-lg-3 mb-8 flex flex-col lg:mb-0">
          <h2 class="alt">{{ section.data.title }}</h2>
        </div>

        <div class="col-lg-8">
          <div class="row items-center">
            <div class="col-md-6 mb-5 lg:mb-0">
              <AppImage
                :src="section.data.image.url"
                :alt="section.data.image.alt_text || section.data.title"
              />
            </div>

            <div class="col-md-6">
              <h3 class="mb-3">{{ section.data.subtitle }}</h3>

              <p class="mb-6 md:mb-10">
                {{ section.data.main_text }}
              </p>

              <AppButton :to="section.data.link" variant="text">
                <template #icon-end-circle>
                  <IconChevronRight />
                </template>
                {{ section.data.link_text }}
              </AppButton>
            </div>
          </div>
        </div>
      </section>

      <div v-if="shouldUseSpacingInsteadOfDivider(index)" class="h-8 md:h-16"></div>
      <AppDivider v-else class="my-8 md:my-16" strong />
    </template>
  </div>
</template>

<style lang="scss">
@import '@boats/ui/styles/config';

.banner-image__hero {
  width: 100%;
  height: 23.75rem;
  background-color: $black;

  @include media-breakpoint-up(md) {
    height: 37.5625rem;
  }
}

.banner-image {
  position: relative;
  margin-top: -5rem;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
  overflow: hidden;
}

.banner-image__carousel {
  .ui-carousel__carousel:not(.flickity-enabled) {
    .banner-image__hero:not(:first-child) {
      transform: translateX(100%);
      position: absolute;
      transition: transform 0.3s ease, opacity 0.3s ease;
    }
  }

  .flickity-cell {
    width: 100%;
  }
}

.banner-image__image {
  position: absolute;
  inset: 0;
  background-color: $black;
}

.banner-image__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.banner-image__overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(92.212deg, #121318 1.98%, rgba(9, 9, 9, 0) 99.15%);
}

.banner-image__full-link {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.banner-image__content {
  position: absolute;
  z-index: 3;
  top: 25%;
  left: 1.5rem;
  right: 1.5rem;
  color: $white;
  pointer-events: none;

  @include media-breakpoint-up(md) {
    top: 3rem;
    left: 5.5rem;
    right: auto;
    max-width: 50rem;
    text-align: left;
  }
}

.banner-image__kicker {
  margin: 0 0 1.25rem;
  color: $red;
}

.banner-image__title {
  margin: 0;
  color: $white;
  // Editors break lines in the CMS textarea (BOAT-2020).
  white-space: pre-line;
  max-width: 20rem;
  margin-left: 0;
  margin-right: auto;

  @include media-breakpoint-up(md) {
    max-width: 50rem;
    margin-left: 0;
    margin-right: 0;
    font-size: 3.75rem;
    line-height: 1.2;
    letter-spacing: -0.01666667em;
  }
}

.banner-image__sub-text {
  margin-top: 1rem;
  color: $white;
}

.banner-image__cta {
  display: inline-flex;
  align-items: center;
  position: absolute;
  z-index: 4;
  left: 1.5rem;
  bottom: 1rem;
  padding: 0;
  color: $white;
  background: none;
  border: 0;
  text-decoration: none !important;
  cursor: pointer;
  pointer-events: auto;

  @include media-breakpoint-up(md) {
    left: 5.5rem;
    bottom: 5rem;
  }
}

// Split panel design (BOAT-2020): a fixed navy panel with the photo on the right fading
// into it on desktop; on mobile the photo goes full-bleed behind a left-dark gradient.
// Sizes follow the 1920×640 / 390×300 design; desktop type scales with the viewport.
$split-panel: #0d0f1a;
// Below md the navbar's language/currency strip overlaps the banner's top 40px; the
// banner grows by that much and centres its copy below it, so 300px stays visible.
$split-mobile-overlap: 2.5rem;

.banner-image--split {
  .banner-image__hero {
    height: calc(18.75rem + #{$split-mobile-overlap});
    background-color: $split-panel;

    @include media-breakpoint-up(md) {
      height: 40rem;
    }
  }

  .banner-image__image {
    background-color: $split-panel;

    @include media-breakpoint-up(md) {
      left: auto;
      width: 52.6%; // 1010 / 1920
    }
  }

  .banner-image__fade {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(90deg, rgba($split-panel, 0.92) 0%, rgba($split-panel, 0.7) 55%, rgba($split-panel, 0.15) 100%);

    @include media-breakpoint-up(md) {
      right: auto;
      left: 47.4%; // 910 / 1920
      width: 15.6%; // 300 / 1920
      background: linear-gradient(90deg, $split-panel 0%, rgba($split-panel, 0.6) 45%, rgba($split-panel, 0) 100%);
    }
  }

  .banner-image__content {
    top: $split-mobile-overlap;
    bottom: 0;
    left: 1.25rem;
    right: 1.25rem;
    max-width: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 0.75rem;
    font-family: 'Archivo', sans-serif;

    @include media-breakpoint-up(md) {
      top: 0;
      left: 6.25%; // 120 / 1920
      right: auto;
      width: 41.7%; // 800 / 1920
      gap: clamp(1rem, 1.5625vw, 1.875rem);
    }
  }

  .banner-image__kicker {
    margin: 0;
    font-family: inherit;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: 0.16em;

    @include media-breakpoint-up(md) {
      font-size: clamp(0.875rem, 1.0417vw, 1.25rem);
      letter-spacing: 0.15em;
    }
  }

  .banner-image__title {
    max-width: none;
    font-family: inherit;
    font-size: 1.875rem;
    font-weight: 500;
    line-height: 1.08;
    letter-spacing: -0.02em;

    @include media-breakpoint-up(md) {
      font-size: clamp(2.5rem, 4.1667vw, 5rem);
      line-height: 1.05;
      letter-spacing: -0.01875em;
    }
  }

  .banner-image__sub-text {
    margin: 0;
    max-width: 17.5rem;
    font-size: 0.875rem;
    line-height: 1.4;
    color: #dcdae0;

    @include media-breakpoint-up(md) {
      max-width: 40rem;
      font-size: clamp(1rem, 1.3542vw, 1.625rem);
      color: #c9c7cf;
    }
  }
}

.banner-image__button {
  margin-top: 0.25rem;
  padding: 0.75rem 1.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: $white;
  border: 2px solid $white;
  border-radius: 6px;
  text-decoration: none !important;
  pointer-events: auto;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover,
  &:focus-visible {
    color: $split-panel;
    background-color: $white;
  }

  @include media-breakpoint-up(md) {
    margin-top: 0;
    padding: clamp(0.75rem, 0.9375vw, 1.125rem) clamp(1.375rem, 1.875vw, 2.25rem);
    font-size: clamp(0.875rem, 1.1458vw, 1.375rem);
    letter-spacing: 0.09em;
  }
}

.banner-image__cta-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 1.5rem;
  height: 1.5rem;
  margin-left: 0.75rem;
  color: $black;
  background-color: $white;
  border-radius: 50%;

  svg {
    width: 1rem;
    height: 1rem;
  }
}

</style>
